import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Lock, Users, GraduationCap, KeySquare } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { generateJoinCode } from '../utils/joinCode';
import { ClassIconPicker } from '../components/ClassIconPicker';

export default function ClassNew() {
  const { user } = useUser();
  const getSupabase = useSupabaseClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState<string | null>('cap');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !name.trim()) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const supabase = await getSupabase();
      for (let attempt = 0; attempt < 5; attempt++) {
        const joinCode = generateJoinCode();
        const { data, error: insertError } = await supabase
          .from('classes')
          .insert({
            teacher_id: user.id,
            name: name.trim(),
            join_code: joinCode,
            requires_approval: requiresApproval,
            icon_key: selectedIcon,
          })
          .select('id')
          .single();

        if (!insertError && data) {
          navigate(`/sinif/${data.id}`);
          return;
        }
        // 23505 = unique_violation (kod çakışması) — yeni kodla tekrar dene
        if (insertError && insertError.code !== '23505') {
          console.error('[ClassNew] insert hatası:', insertError);
          setError(`Hata (${insertError.code ?? 'bilinmiyor'}): ${insertError.message}`);
          break;
        }
      }
    } catch (err: any) {
      console.error('[ClassNew] beklenmedik hata:', err);
      setError(err?.message ?? 'Bilinmeyen bir hata oluştu.');
    }
    setIsSubmitting(false);
  }

  return (
    <div className="p-4 md:p-6 lg:p-10 max-w-lg mx-auto">
      <Helmet>
        <title>Yeni Sınıf Oluştur | Bitmiyo Sınıf</title>
        <meta name="description" content="Öğrencileriniz için yeni bir koçluk sınıfı veya çalışma grubu oluşturun." />
      </Helmet>

      <Link
        to="/panel"
        className="inline-flex items-center gap-1.5 text-text-muted text-sm mb-5 hover:text-accent-600 transition-colors"
      >
        <ArrowLeft size={14} /> Panele dön
      </Link>

      <div className="flex items-center gap-3 mb-1">
        <GraduationCap size={24} className="text-accent-500 shrink-0" />
        <h1 className="text-2xl font-black text-text-primary" style={{ fontFamily: 'var(--font-heading)' }}>
          Yeni Sınıf
        </h1>
      </div>
      <p className="text-text-secondary text-sm mb-6">
        Sınıfını oluştur, öğrencilerin katılma kodunu paylaş.
      </p>

      <Card className="p-6 animate-fade-in-up">
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">

          {/* Sınıf Adı */}
          <div>
            <label className="text-sm font-bold text-text-primary mb-1.5 block" htmlFor="class-name">
              Sınıf Adı
            </label>
            <input
              id="class-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="ör. 9. Sınıf Matematik Koçluğu"
              className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-sm outline-none focus:border-accent-400 transition-colors"
            />
          </div>

          {/* 3D Sınıf İkonu Seçimi */}
          <ClassIconPicker selectedIcon={selectedIcon} onChange={setSelectedIcon} />

          {/* Katılım Türü */}
          <div>
            <p className="text-sm font-bold text-text-primary mb-2">Katılım Türü</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRequiresApproval(false)}
                className={`text-left p-4 rounded-xl border-2 transition-all active:scale-[0.97] ${
                  !requiresApproval
                    ? 'border-accent-500 bg-accent-100 dark:bg-accent-500/10'
                    : 'border-border hover:border-border/60'
                }`}
              >
                <Users size={22} className={`mb-2 ${!requiresApproval ? 'text-accent-500' : 'text-text-secondary'}`} />
                <p className="font-bold text-sm text-text-primary">Açık</p>
                <p className="text-text-secondary text-xs mt-0.5">Kodu bilen herkes anında katılır.</p>
              </button>
              <button
                type="button"
                onClick={() => setRequiresApproval(true)}
                className={`text-left p-4 rounded-xl border-2 transition-all active:scale-[0.97] ${
                  requiresApproval
                    ? 'border-accent-500 bg-accent-100 dark:bg-accent-500/10'
                    : 'border-border hover:border-border/60'
                }`}
              >
                <Lock size={22} className={`mb-2 ${requiresApproval ? 'text-accent-500' : 'text-text-secondary'}`} />
                <p className="font-bold text-sm text-text-primary">Onaylı</p>
                <p className="text-text-secondary text-xs mt-0.5">Kod girişi seni onaylamanı bekler.</p>
              </button>
            </div>
          </div>

          {error && (
            <p className="text-danger-500 text-sm font-semibold animate-fade-in-up">{error}</p>
          )}

          <Button type="submit" size="lg" disabled={isSubmitting || !name.trim()}>
            {isSubmitting ? 'Oluşturuluyor...' : 'Sınıfı Oluştur'}
          </Button>
        </form>
      </Card>

      <div className="mt-4 flex items-start gap-3 px-1">
        <KeySquare size={16} className="text-text-muted shrink-0 mt-0.5" />
        <p className="text-text-muted text-xs leading-relaxed">
          Sınıf oluşturulduğunda otomatik bir katılma kodu üretilir; bu kodu öğrencilerinle paylaşarak sınıfına davet edebilirsin.
        </p>
      </div>
    </div>
  );
}
